import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'wouter';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import ClientsTable from './ClientsTable';
import clientSelector from '../clientSelector';
import { loadClients, addClient, deleteClient } from '../clientsReducer';
import transformer from '../../common/helpers/transformer';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import type { AppDispatch } from '../../store';
import { initClient } from './initClient';
import { getClientPayloadType } from '../../common/helpers/payloadType';
import ConfirmationDialog from '../../common/components/ConfirmationDialog';

function Clients() {
  const [location, setLocation] = useLocation();
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const dispatch: AppDispatch = useDispatch();

  const clients = useSelector(clientSelector.getClients);
  const status = useSelector(clientSelector.getStatus);
  const payloadType = getClientPayloadType(location);

  const [searchValue, setSearchValue] = React.useState('');

  React.useEffect(() => {
    dispatch(loadClients(payloadType));
  }, []);

  return (
    <Stack px={3} py={4} spacing={2} height='100%' overflow='hidden'>
      <Stack direction='row' alignItems='center' width='100%'>
        <Typography fontSize='1.5rem'>Klient*innen</Typography>
        <Button
          sx={{ margin: '0 16px' }}
          aria-label='Klientin hinzufügen'
          startIcon={<AddCircleIcon />}
          variant='contained'
          onClick={async () => {
            const id = uuidv4();
            const clientPayload = (await dispatch(loadClients(payloadType))).payload;
            const currentClients = Array.isArray(clientPayload) ? clientPayload : [];
            const client = initClient(currentClients, location);
            const payload = await transformer.fromObjectToBase64WithEncryption(client);

            await dispatch(addClient({ id, payloadType, payload }));
            await dispatch(loadClients(payloadType));

            setLocation(`${location}/${id}`);
          }}
        >
          Klient*in hinzufügen
        </Button>

        <Stack direction='row' alignItems='center' ml='auto'>
          {selectedClientIds.length > 0 && (
            <ConfirmationDialog
              button={(onClick) => (
                <Button
                  variant='outlined'
                  startIcon={<DeleteIcon />}
                  sx={{ marginRight: 5 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                  }}
                >
                  Klient:innen löschen
                </Button>
              )}
              dialogTitle='Ausgewählte Klient*innen löschen'
              dialogText={`Möchtest du die ${selectedClientIds.length} ${
                selectedClientIds.length === 1 ? 'ausgewählte' : 'ausgewählten'
              } Klient*innen wirklich löschen?`}
              dialogConfirmText='Löschen'
              onConfirm={async () => {
                await Promise.all(selectedClientIds.map((id) => dispatch(deleteClient(id))));
                await dispatch(loadClients(payloadType));
                setSelectedClientIds([]);
              }}
            />
          )}
          <TextField
            label='Suche'
            type='search'
            variant='standard'
            sx={{ width: '300px' }}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </Stack>
      </Stack>
      {status === 'loading' ? (
        <LoadingIndicator />
      ) : (
        <ClientsTable
          searchValue={searchValue}
          clients={clients}
          payloadType={payloadType}
          selectedClientIds={selectedClientIds}
          setSelectedClientIds={setSelectedClientIds}
        />
      )}
    </Stack>
  );
}

export default Clients;
